import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { BasicService } from 'src/service/basic.service';
import { MasterCatalogFilters, MasterCatalogService, MasterSuppliersFilters } from 'src/service/master-catalog.service';
import { Cr02DialogService } from 'src/app/shared/cr02-dialog/cr02-dialog.service';

@Component({
  selector: 'app-master-catalog',
  templateUrl: './master-catalog.component.html',
  styleUrls: ['./master-catalog.component.scss']
})
export class MasterCatalogComponent implements OnInit, OnDestroy {

  activeMenu: 'items' | 'suppliers' = 'items';

  rows: any[] = [];
  totalrec = 0;
  distinctItemCount = 0;
  index = 1;
  num_of_pages = 0;
  itemsPerPage = 10;
  currentPage = 1;
  rowsPerOptions = [10, 25, 50, 100];
  gotoInput = '';
  gotoError = false;
  supplierGotoInput = '';
  supplierGotoError = false;
  supplierNames: string[] = [];
  masterSyncInProgress = false;
  masterSyncStopRequested = false;
  syncPanelVisible = false;
  syncJob: any = null;
  private syncStatusTimer: ReturnType<typeof setTimeout> | null = null;

  filters: MasterCatalogFilters = {
    item_code: '',
    item_name: '',
    barcode: '',
    vendor_code: '',
    vendor_name: '',
    vendor_item_code: ''
  };

  supplierRows: any[] = [];
  supplierTotal = 0;
  supplierCurrentPage = 1;
  supplierIndex = 1;
  supplierNumPages = 0;
  supplierSyncInProgress = false;
  supplierSyncStopRequested = false;
  supplierSyncPanelVisible = false;
  supplierSyncJob: any = null;
  private supplierSyncStatusTimer: ReturnType<typeof setTimeout> | null = null;

  supplierFilters: MasterSuppliersFilters = {
    supplier_id: '',
    supplier_name: '',
    supplier_erp_code: '',
    status: ''
  };

  constructor(
    public basicService: BasicService,
    private masterCatalogService: MasterCatalogService,
    private router: Router,
    private dialog: Cr02DialogService
  ) {
    if (this.basicService.user_data?.path_name?.toLowerCase() !== 'admin') {
      this.router.navigate(['/login']);
    }
  }

  ngOnInit(): void {
    this.loadSupplierNames();
    this.loadCatalog(0);
  }

  ngOnDestroy(): void {
    this.clearSyncStatusTimer();
    this.clearSupplierSyncStatusTimer();
  }

  setActiveMenu(menu: 'items' | 'suppliers'): void {
    if (this.activeMenu === menu) {
      return;
    }
    this.activeMenu = menu;
    if (menu === 'suppliers' && this.supplierRows.length === 0) {
      this.loadSuppliers(0);
    }
  }

  private clearSyncStatusTimer(): void {
    if (this.syncStatusTimer) {
      clearTimeout(this.syncStatusTimer);
      this.syncStatusTimer = null;
    }
  }

  loadSupplierNames(): void {
    this.masterCatalogService.getVendorNames().subscribe({
      next: (response: any) => {
        if (response?.status === 'success' && Array.isArray(response.data)) {
          this.supplierNames = response.data;
        }
      },
      error: () => {
        this.supplierNames = [];
      }
    });
  }

  calculatePages(): void {
    this.num_of_pages = Math.ceil(this.totalrec / this.itemsPerPage);
    if (this.num_of_pages === 0) {
      this.num_of_pages = 1;
    }
  }

  get itemsRangeLabel(): string {
    if (!this.totalrec) {
      return 'No catalog rows match your filters';
    }
    const start = (this.currentPage - 1) * this.itemsPerPage + 1;
    const end = Math.min(start + this.rows.length - 1, this.totalrec);
    return `Showing ${start.toLocaleString()}–${end.toLocaleString()} of ${this.totalrec.toLocaleString()} rows`;
  }

  get suppliersRangeLabel(): string {
    if (!this.supplierTotal) {
      return 'No suppliers match your filters';
    }
    const start = (this.supplierCurrentPage - 1) * this.itemsPerPage + 1;
    const end = Math.min(start + this.supplierRows.length - 1, this.supplierTotal);
    return `Showing ${start.toLocaleString()}–${end.toLocaleString()} of ${this.supplierTotal.toLocaleString()} suppliers`;
  }

  get itemsGotoPlaceholder(): string {
    return `1–${this.num_of_pages || 1}`;
  }

  get suppliersGotoPlaceholder(): string {
    return `1–${this.supplierNumPages || 1}`;
  }

  get itemsPagerItems(): Array<number | '…'> {
    return this.buildPagerItems(this.currentPage, this.num_of_pages);
  }

  get suppliersPagerItems(): Array<number | '…'> {
    return this.buildPagerItems(this.supplierCurrentPage, this.supplierNumPages);
  }

  private buildPagerItems(current: number, totalPages: number): Array<number | '…'> {
    const total = Math.max(1, totalPages || 1);
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    const set = new Set<number>([1, total, current - 1, current, current + 1]);
    if (current <= 3) {
      [2, 3, 4].forEach((p) => set.add(p));
    }
    if (current >= total - 2) {
      [total - 3, total - 2, total - 1].forEach((p) => set.add(p));
    }
    const pages = [...set].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
    const out: Array<number | '…'> = [];
    pages.forEach((p, i) => {
      if (i > 0 && p - pages[i - 1] > 1) {
        out.push('…');
      }
      out.push(p);
    });
    return out;
  }

  onRowsPerChange(value: string | number): void {
    const next = Number(value);
    if (!this.rowsPerOptions.includes(next)) {
      return;
    }
    this.itemsPerPage = next;
    if (this.activeMenu === 'items') {
      this.gotoInput = '';
      this.gotoError = false;
      this.loadCatalog(0);
    } else {
      this.supplierGotoInput = '';
      this.supplierGotoError = false;
      this.loadSuppliers(0);
    }
  }

  goToItemsPage(page: number | string): void {
    const pageNum = Math.floor(Number(page));
    if (!Number.isFinite(pageNum) || pageNum < 1) {
      return;
    }
    if (this.num_of_pages > 0 && pageNum > this.num_of_pages) {
      return;
    }
    if (pageNum === this.currentPage) {
      return;
    }
    this.loadCatalog((pageNum - 1) * this.itemsPerPage);
  }

  goToSuppliersPage(page: number | string): void {
    const pageNum = Math.floor(Number(page));
    if (!Number.isFinite(pageNum) || pageNum < 1) {
      return;
    }
    if (this.supplierNumPages > 0 && pageNum > this.supplierNumPages) {
      return;
    }
    if (pageNum === this.supplierCurrentPage) {
      return;
    }
    this.loadSuppliers((pageNum - 1) * this.itemsPerPage);
  }

  submitItemsGotoPage(): void {
    const total = Math.max(1, this.num_of_pages);
    const value = parseInt(this.gotoInput, 10);
    if (!value || value < 1 || value > total) {
      this.gotoError = true;
      setTimeout(() => {
        this.gotoError = false;
      }, 1800);
      return;
    }
    this.gotoInput = '';
    this.goToItemsPage(value);
  }

  submitSuppliersGotoPage(): void {
    const total = Math.max(1, this.supplierNumPages);
    const value = parseInt(this.supplierGotoInput, 10);
    if (!value || value < 1 || value > total) {
      this.supplierGotoError = true;
      setTimeout(() => {
        this.supplierGotoError = false;
      }, 1800);
      return;
    }
    this.supplierGotoInput = '';
    this.goToSuppliersPage(value);
  }

  onItemsGotoKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.submitItemsGotoPage();
    }
  }

  onSuppliersGotoKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.submitSuppliersGotoPage();
    }
  }

  nextPage(): void {
    if (this.currentPage < this.num_of_pages) {
      this.goToItemsPage(this.currentPage + 1);
    }
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.goToItemsPage(this.currentPage - 1);
    }
  }

  applyFilters(): void {
    this.loadCatalog(0);
  }

  syncMasterRecent(): void {
    this.startMasterSync(this.masterCatalogService.syncMasterRecent(), 'Recent sync');
  }

  syncMasterFull(): void {
    this.dialog.confirm({
      title: 'Full master sync?',
      message: 'This syncs all master data from 1900-01-01. It may take a long time.',
      confirmText: 'Start full sync',
      cancelText: 'Cancel',
      type: 'warning'
    }).then((ok) => {
      if (ok) {
        this.startMasterSync(this.masterCatalogService.syncMasterFull(), 'Full sync');
      }
    });
  }

  private startMasterSync(syncRequest: { subscribe: Function }, syncMode: string): void {
    if (this.masterSyncInProgress) {
      return;
    }
    this.masterSyncInProgress = true;
    this.syncPanelVisible = true;
    this.syncJob = {
      status: 'running',
      sync_mode: syncMode,
      progress: { stage: 'queued' }
    };
    syncRequest.subscribe({
      next: (response: any) => {
        if (response?.ok && response.job_id) {
          this.syncJob = {
            ...this.syncJob,
            job_id: response.job_id,
            from_date: response.from_date,
            to_date: response.to_date,
            sync_mode: syncMode,
            status: 'running',
            progress: { stage: 'queued' }
          };
          this.pollSyncStatus(response.job_id);
          return;
        }
        this.finishSync(false, response?.message || 'Failed to start master sync');
      },
      error: (error: any) => {
        this.finishSync(false, error.error?.message || 'Failed to start master sync');
      }
    });
  }

  stopMasterSync(): void {
    const jobId = this.syncJob?.job_id;
    if (!jobId || !this.masterSyncInProgress || this.masterSyncStopRequested) {
      return;
    }
    this.masterSyncStopRequested = true;
    this.masterCatalogService.cancelSyncMaster(jobId).subscribe({
      next: (response: any) => {
        if (!response?.ok) {
          this.masterSyncStopRequested = false;
          this.dialog.error('Could not stop sync', response?.message || 'Failed to request stop');
        }
      },
      error: (error: any) => {
        this.masterSyncStopRequested = false;
        this.dialog.error('Could not stop sync', error.error?.message || 'Failed to request stop');
      }
    });
  }

  dismissSyncPanel(): void {
    if (!this.masterSyncInProgress) {
      this.syncPanelVisible = false;
      this.syncJob = null;
    }
  }

  private pollSyncStatus(jobId: string): void {
    this.masterCatalogService.getSyncMasterStatus(jobId).subscribe({
      next: (response: any) => {
        const job = response?.job;
        if (job) {
          const syncMode = this.syncJob?.sync_mode;
          this.syncJob = syncMode ? { ...job, sync_mode: syncMode } : job;
        }
        const status = job?.status;
        if (status === 'running') {
          this.syncStatusTimer = setTimeout(() => this.pollSyncStatus(jobId), 1500);
          return;
        }
        if (status === 'success') {
          const msg = job?.result?.message || 'Master sync completed successfully';
          this.finishSync(true, msg);
          return;
        }
        if (status === 'cancelled') {
          const msg = job?.result?.message || 'Master sync stopped';
          this.finishSync(false, msg, true);
          return;
        }
        const err = job?.error || job?.result?.message || 'Master sync failed';
        this.finishSync(false, err);
      },
      error: (error: any) => {
        this.finishSync(false, error.error?.message || 'Failed to check sync status');
      }
    });
  }

  private finishSync(success: boolean, message: string, cancelled: boolean = false): void {
    this.clearSyncStatusTimer();
    this.masterSyncInProgress = false;
    this.masterSyncStopRequested = false;
    if (this.syncJob) {
      this.syncJob = {
        ...this.syncJob,
        status: success ? 'success' : (cancelled ? 'cancelled' : 'failed'),
        finish_message: message
      };
    }
    if (success || cancelled) {
      this.loadSupplierNames();
      this.loadCatalog(0, false);
    }
  }

  syncStageLabel(): string {
    const stage = this.syncJob?.progress?.stage || this.syncJob?.status || '';
    const labels: Record<string, string> = {
      queued: 'Queued',
      started: 'Started',
      fetching: 'Fetching from API',
      processing: 'Saving to database',
      completed: 'Completed',
      failed: 'Failed',
      cancelled: 'Stopped'
    };
    return labels[stage] || String(stage || '—');
  }

  syncStatusBadgeClass(): string {
    const status = this.syncJob?.status;
    if (status === 'success') return 'b-ok';
    if (status === 'failed') return 'b-err';
    if (status === 'cancelled') return 'b-muted';
    if (this.masterSyncStopRequested) return 'b-info';
    return 'b-run';
  }

  statusBadgeClass(value: any): string {
    const raw = String(value ?? '').trim().toLowerCase();
    if (!raw || raw === '-') return 'b-muted';
    if (raw === '1' || raw === 'active' || raw === 'a' || raw === 'y' || raw === 'yes') return 'b-ok';
    if (raw === '0' || raw === 'inactive' || raw === 'i' || raw === 'n' || raw === 'no') return 'b-muted';
    if (raw.includes('fail') || raw.includes('error') || raw.includes('block')) return 'b-err';
    if (raw.includes('pend') || raw.includes('wait')) return 'b-warn';
    return 'b-muted';
  }

  supplierRowStatusClass(value: any): string {
    if (value === 1 || value === '1') return 'b-ok';
    if (value === 0 || value === '0') return 'b-muted';
    return 'b-muted';
  }

  syncStatusLabel(): string {
    const status = this.syncJob?.status;
    if (status === 'success') return 'Completed';
    if (status === 'failed') return 'Failed';
    if (status === 'cancelled') return 'Stopped';
    if (this.masterSyncStopRequested) return 'Stopping…';
    return 'Running';
  }

  /** API totalPages plus one extra fetch (see master_sync.py). */
  private syncFetchPageCount(): number {
    const total = this.syncJob?.progress?.total_pages;
    if (total == null || total <= 0) {
      return 0;
    }
    return total + 1;
  }

  syncPageProgressPercent(): number {
    const fetchTotal = this.syncFetchPageCount();
    if (!fetchTotal) {
      return 0;
    }
    const current = (this.syncJob?.progress?.current_page ?? 0) + 1;
    return Math.min(100, Math.round((current / fetchTotal) * 100));
  }

  syncPageProgressText(): string {
    const fetchTotal = this.syncFetchPageCount();
    if (!fetchTotal) {
      return 'Page —';
    }
    const current = (this.syncJob?.progress?.current_page ?? 0) + 1;
    return `Page ${current} of ${fetchTotal}`;
  }

  syncCounts() {
    const p = this.syncJob?.progress || {};
    const r = this.syncJob?.result || {};
    return {
      items: r.items_processed ?? p.items_processed ?? 0,
      barcodes: r.barcodes_processed ?? p.barcodes_processed ?? 0,
      vendors: r.vendors_processed ?? p.vendors_processed ?? 0
    };
  }

  clearFilters(): void {
    this.filters = {
      item_code: '',
      item_name: '',
      barcode: '',
      vendor_code: '',
      vendor_name: '',
      vendor_item_code: ''
    };
    this.loadCatalog(0);
  }

  loadCatalog(offset: number = 0, showLoader: boolean = true): void {
    if (showLoader && !this.masterSyncInProgress) {
      this.basicService.showprogress = true;
    }
    this.masterCatalogService.getMasterCatalog(offset, this.filters, this.itemsPerPage).toPromise()
      .then((response: any) => {
        if (showLoader && !this.masterSyncInProgress) {
          this.basicService.showprogress = false;
        }
        if (response.status === 'success') {
          this.rows = response.data || [];
          this.totalrec = response.total_count != null ? response.total_count : 0;
          this.distinctItemCount = response.distinct_item_count != null ? response.distinct_item_count : 0;
          this.currentPage = Math.floor(offset / this.itemsPerPage) + 1;
          this.index = this.currentPage;
          this.calculatePages();
        } else {
          this.dialog.error('Error', response.message || 'Failed to load master catalog');
          this.rows = [];
          this.totalrec = 0;
          this.distinctItemCount = 0;
          this.calculatePages();
        }
      })
      .catch((error: any) => {
        if (showLoader && !this.masterSyncInProgress) {
          this.basicService.showprogress = false;
        }
        this.dialog.error('Error', error.error?.message || 'Failed to load master catalog');
        this.rows = [];
        this.totalrec = 0;
        this.distinctItemCount = 0;
        this.calculatePages();
      });
  }

  displayValue(value: any): string {
    if (value === null || value === undefined || String(value).trim() === '') {
      return '-';
    }
    return String(value);
  }

  displaySupplierStatus(value: any): string {
    if (value === 1 || value === '1') {
      return 'Active';
    }
    if (value === 0 || value === '0') {
      return 'Inactive';
    }
    return this.displayValue(value);
  }

  calculateSupplierPages(): void {
    this.supplierNumPages = Math.ceil(this.supplierTotal / this.itemsPerPage);
    if (this.supplierNumPages === 0) {
      this.supplierNumPages = 1;
    }
  }

  nextSupplierPage(): void {
    if (this.supplierCurrentPage < this.supplierNumPages) {
      this.goToSuppliersPage(this.supplierCurrentPage + 1);
    }
  }

  prevSupplierPage(): void {
    if (this.supplierCurrentPage > 1) {
      this.goToSuppliersPage(this.supplierCurrentPage - 1);
    }
  }

  applySupplierFilters(): void {
    this.loadSuppliers(0);
  }

  clearSupplierFilters(): void {
    this.supplierFilters = {
      supplier_id: '',
      supplier_name: '',
      supplier_erp_code: '',
      status: ''
    };
    this.loadSuppliers(0);
  }

  syncMasterSuppliers(): void {
    if (this.supplierSyncInProgress) {
      return;
    }
    this.supplierSyncInProgress = true;
    this.supplierSyncPanelVisible = true;
    this.supplierSyncJob = {
      status: 'running',
      sync_mode: 'Supplier sync',
      progress: { stage: 'queued' }
    };
    this.masterCatalogService.syncMasterSuppliers().subscribe({
      next: (response: any) => {
        if (response?.ok && response.job_id) {
          this.supplierSyncJob = {
            ...this.supplierSyncJob,
            job_id: response.job_id,
            status: 'running',
            progress: { stage: 'queued' }
          };
          this.pollSupplierSyncStatus(response.job_id);
          return;
        }
        this.finishSupplierSync(false, response?.message || 'Failed to start supplier sync');
      },
      error: (error: any) => {
        this.finishSupplierSync(false, error.error?.message || 'Failed to start supplier sync');
      }
    });
  }

  stopSupplierSync(): void {
    const jobId = this.supplierSyncJob?.job_id;
    if (!jobId || !this.supplierSyncInProgress || this.supplierSyncStopRequested) {
      return;
    }
    this.supplierSyncStopRequested = true;
    this.masterCatalogService.cancelSyncMasterSuppliers(jobId).subscribe({
      next: (response: any) => {
        if (!response?.ok) {
          this.supplierSyncStopRequested = false;
          this.dialog.error('Could not stop sync', response?.message || 'Failed to request stop');
        }
      },
      error: (error: any) => {
        this.supplierSyncStopRequested = false;
        this.dialog.error('Could not stop sync', error.error?.message || 'Failed to request stop');
      }
    });
  }

  dismissSupplierSyncPanel(): void {
    if (!this.supplierSyncInProgress) {
      this.supplierSyncPanelVisible = false;
      this.supplierSyncJob = null;
    }
  }

  private clearSupplierSyncStatusTimer(): void {
    if (this.supplierSyncStatusTimer) {
      clearTimeout(this.supplierSyncStatusTimer);
      this.supplierSyncStatusTimer = null;
    }
  }

  private pollSupplierSyncStatus(jobId: string): void {
    this.masterCatalogService.getSyncMasterSuppliersStatus(jobId).subscribe({
      next: (response: any) => {
        const job = response?.job;
        if (job) {
          this.supplierSyncJob = { ...job, sync_mode: 'Supplier sync' };
        }
        const status = job?.status;
        if (status === 'running') {
          this.supplierSyncStatusTimer = setTimeout(() => this.pollSupplierSyncStatus(jobId), 1500);
          return;
        }
        if (status === 'success') {
          this.finishSupplierSync(true, job?.result?.message || 'Supplier sync completed successfully');
          return;
        }
        if (status === 'cancelled') {
          this.finishSupplierSync(false, job?.result?.message || 'Supplier sync stopped', true);
          return;
        }
        const err = job?.error || job?.result?.message || 'Supplier sync failed';
        this.finishSupplierSync(false, err);
      },
      error: (error: any) => {
        this.finishSupplierSync(false, error.error?.message || 'Failed to check sync status');
      }
    });
  }

  private finishSupplierSync(success: boolean, message: string, cancelled: boolean = false): void {
    this.clearSupplierSyncStatusTimer();
    this.supplierSyncInProgress = false;
    this.supplierSyncStopRequested = false;
    if (this.supplierSyncJob) {
      this.supplierSyncJob = {
        ...this.supplierSyncJob,
        status: success ? 'success' : (cancelled ? 'cancelled' : 'failed'),
        finish_message: message
      };
    }
    if (success || cancelled) {
      this.loadSuppliers(0, false);
    }
  }

  supplierSyncStageLabel(): string {
    const stage = this.supplierSyncJob?.progress?.stage || this.supplierSyncJob?.status || '';
    const labels: Record<string, string> = {
      queued: 'Queued',
      started: 'Started',
      fetching: 'Fetching from API',
      processing: 'Saving to database',
      completed: 'Completed',
      failed: 'Failed',
      cancelled: 'Stopped'
    };
    return labels[stage] || String(stage || '—');
  }

  supplierSyncStatusBadgeClass(): string {
    const status = this.supplierSyncJob?.status;
    if (status === 'success') return 'b-ok';
    if (status === 'failed') return 'b-err';
    if (status === 'cancelled') return 'b-muted';
    if (this.supplierSyncStopRequested) return 'b-info';
    return 'b-run';
  }

  supplierSyncStatusLabel(): string {
    const status = this.supplierSyncJob?.status;
    if (status === 'success') return 'Completed';
    if (status === 'failed') return 'Failed';
    if (status === 'cancelled') return 'Stopped';
    if (this.supplierSyncStopRequested) return 'Stopping…';
    return 'Running';
  }

  supplierSyncCounts() {
    const p = this.supplierSyncJob?.progress || {};
    const r = this.supplierSyncJob?.result || {};
    return {
      suppliers: r.suppliers_processed ?? p.suppliers_processed ?? 0,
      batches: r.batches ?? p.batches ?? 0
    };
  }

  loadSuppliers(offset: number = 0, showLoader: boolean = true): void {
    if (showLoader && !this.supplierSyncInProgress) {
      this.basicService.showprogress = true;
    }
    this.masterCatalogService.getMasterSuppliers(offset, this.supplierFilters, this.itemsPerPage).toPromise()
      .then((response: any) => {
        if (showLoader && !this.supplierSyncInProgress) {
          this.basicService.showprogress = false;
        }
        if (response.status === 'success') {
          this.supplierRows = response.data || [];
          this.supplierTotal = response.total_count != null ? response.total_count : 0;
          this.supplierCurrentPage = Math.floor(offset / this.itemsPerPage) + 1;
          this.supplierIndex = this.supplierCurrentPage;
          this.calculateSupplierPages();
        } else {
          this.dialog.error('Error', response.message || 'Failed to load master suppliers');
          this.supplierRows = [];
          this.supplierTotal = 0;
          this.calculateSupplierPages();
        }
      })
      .catch((error: any) => {
        if (showLoader && !this.supplierSyncInProgress) {
          this.basicService.showprogress = false;
        }
        this.dialog.error('Error', error.error?.message || 'Failed to load master suppliers');
        this.supplierRows = [];
        this.supplierTotal = 0;
        this.calculateSupplierPages();
      });
  }
}
